/* =========================================================================
   ClueVine — navigation
   A tiny screen state-machine (no router dependency). Top-level destinations
   resolve to a two-deep stack [home, target] so Back always returns home; Play
   is reached by starting a game. How-to / result / score-breakdown are modals,
   not screens, so they don't live here.

   The stack is mirrored into browser history: opening a screen pushes one
   entry, so the browser / Android back gesture returns home instead of leaving
   the app, and a reload lands back on the same screen. The admin screen is
   also addressable as /#admin (the hash is shown only while it's open).
   ========================================================================= */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type Screen = 'home' | 'play' | 'stats' | 'history' | 'settings' | 'howto' | 'admin';

export interface Route {
  screen: Screen;
  params: { modeId?: string };
}

interface NavContextValue {
  route: Route;
  navigate: (screen: Screen, params?: Route['params']) => void;
  back: () => void;
  canBack: boolean;
}

const NavContext = createContext<NavContextValue | null>(null);
const HOME: Route = { screen: 'home', params: {} };
const SCREENS: readonly Screen[] = ['home', 'play', 'stats', 'history', 'settings', 'howto', 'admin'];
const ADMIN_HASH = '#admin';

interface HistoryState {
  cv?: Screen;
}

function isScreen(v: unknown): v is Screen {
  return typeof v === 'string' && (SCREENS as readonly string[]).includes(v);
}

function currentPath(): string {
  return window.location.pathname + window.location.search;
}

function urlFor(screen: Screen): string {
  return currentPath() + (screen === 'admin' ? ADMIN_HASH : '');
}

/** Work out the starting screen, normalizing the URL/history so Back behaves. */
function initialStack(): Route[] {
  if (typeof window === 'undefined') return [HOME];
  try {
    const wantsAdmin = window.location.hash.toLowerCase() === ADMIN_HASH;
    const restored = (window.history.state as HistoryState | null)?.cv;
    const screen: Screen | null = wantsAdmin ? 'admin' : isScreen(restored) && restored !== 'admin' ? restored : null;
    if (!screen || screen === 'home') return [HOME];
    if (wantsAdmin) {
      // Make the entry underneath a plain home entry, then push admin on top of it.
      window.history.replaceState({}, '', currentPath());
      window.history.pushState({ cv: 'admin' } satisfies HistoryState, '', urlFor('admin'));
    }
    return [HOME, { screen, params: {} }];
  } catch {
    return [HOME];
  }
}

export function NavProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<Route[]>(initialStack);
  const stackRef = useRef(stack);
  stackRef.current = stack;

  const navigate = useCallback((screen: Screen, params: Route['params'] = {}) => {
    const onHome = stackRef.current.length === 1;
    try {
      if (screen === 'home') {
        // Pop the entry we pushed, so the history doesn't grow with every visit.
        if (!onHome && (window.history.state as HistoryState | null)?.cv) window.history.back();
        else if (window.location.hash.toLowerCase() === ADMIN_HASH) window.history.replaceState({}, '', currentPath());
      } else if (onHome) {
        window.history.pushState({ cv: screen } satisfies HistoryState, '', urlFor(screen));
      } else {
        window.history.replaceState({ cv: screen } satisfies HistoryState, '', urlFor(screen));
      }
    } catch {
      /* history unavailable (e.g. sandboxed) — in-app navigation still works */
    }
    setStack(screen === 'home' ? [HOME] : [HOME, { screen, params }]);
  }, []);

  const back = useCallback(() => navigate('home'), [navigate]);

  // Browser back/forward and typed #admin URLs.
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      const screen = (e.state as HistoryState | null)?.cv;
      setStack(isScreen(screen) && screen !== 'home' ? [HOME, { screen, params: {} }] : [HOME]);
    };
    const onHash = () => {
      if (window.location.hash.toLowerCase() === ADMIN_HASH && stackRef.current[stackRef.current.length - 1]?.screen !== 'admin') {
        window.history.replaceState({ cv: 'admin' } satisfies HistoryState, '', urlFor('admin'));
        setStack([HOME, { screen: 'admin', params: {} }]);
      }
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onHash);
    };
  }, []);

  const route = stack[stack.length - 1];
  const value = useMemo<NavContextValue>(
    () => ({ route, navigate, back, canBack: stack.length > 1 }),
    [route, navigate, back, stack.length],
  );

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

export function useNav(): NavContextValue {
  const ctx = useContext(NavContext);
  if (!ctx) throw new Error('useNav must be used within <NavProvider>');
  return ctx;
}
